import { graphqlRequest, cmsRestJson } from "../client";
import {
  GET_APIS,
  GET_API,
  GET_CATEGORIES,
  GET_CATEGORY,
  GET_DOCUMENTS,
  GET_DOCUMENT,
  GET_FAQS,
  GET_FAQ,
  GET_DEVELOPER_GUIDES,
  GET_DEVELOPER_GUIDE,
  GET_LINKS,
  GET_LINK,
  GET_PRODUCTS,
  GET_PRODUCT_CATALOG,
  GET_PRODUCT,
  GET_MY_SUBSCRIPTIONS,
  GET_SUBSCRIPTION_REQUESTS,
  GET_APP_KYCS,
  GET_APP_KYC,
  GET_GATEWAY_SKUS,
  GET_GATEWAY_SKU,
  GET_GATEWAY_ROUTES,
  GET_GATEWAY_ROUTE,
  SEARCH_GATEWAY_ROUTES,
  POTENTIAL_DUPLICATE_GATEWAY_ROUTES,
  GET_MY_SUPPORT_CASES,
  GET_AGENT_SUPPORT_CASES,
  GET_SUPPORT_CASE,
  GET_TENANT_SUPPORT_CASE_SETTINGS,
  GET_TENANT_ORGANIZATION_SETTINGS,
  GET_TENANT_MEMBERS,
  GET_TENANT_DATA_EXPORT,
  GET_TENANT_CONTACT_INFO,
  GET_PLATFORM_TENANT_ORGANIZATION_SETTINGS,
  GET_PLATFORM_TENANT_ADMINS,
  GET_PLATFORM_TENANTS,
  GET_PLATFORM_TENANT_INSIGHTS,
  GET_PLATFORM_AGENT_RUNNER_BINDINGS,
  RESOLVED_AGENT_RUNNER_BASE,
  GET_OPERATIONAL_ERRORS,
  SEARCH_OPERATIONAL_ERRORS,
  GET_OPERATIONAL_ERROR,
  GET_KAFKA_EVENT_TOPICS,
  SEARCH_KAFKA_EVENT_TOPICS,
  GET_KAFKA_EVENT_TOPIC,
  GET_API_INTAKES,
  GET_API_INTAKE,
  GET_PRODUCT_MANAGEMENT_GROUPS,
  GET_PRODUCT_MANAGEMENT_GROUP,
  GET_ENTITY_PMG_ASSIGNMENTS,
  GET_PMG_ASSIGNED_ASSETS,
} from "../queries/queries";
import {
  CREATE_API,
  UPDATE_API,
  CREATE_CATEGORY,
  UPDATE_CATEGORY,
  CREATE_DOCUMENT,
  UPDATE_DOCUMENT,
  RETRY_DOCUMENT_RAG,
  SEED_ARCHITECTURE_STANDARDS,
  CREATE_FAQ,
  UPDATE_FAQ,
  CREATE_DEVELOPER_GUIDE,
  UPDATE_DEVELOPER_GUIDE,
  DELETE_DEVELOPER_GUIDE,
  CREATE_LINK,
  UPDATE_LINK,
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
  CREATE_SUBSCRIPTION,
  APPROVE_SUBSCRIPTION,
  REVOKE_SUBSCRIPTION,
  APPROVE_SUBSCRIPTION_PRODUCT,
  REVOKE_SUBSCRIPTION_PRODUCT,
  DELETE_API,
  DELETE_CATEGORY,
  DELETE_DOCUMENT,
  DELETE_FAQ,
  DELETE_LINK,
  DELETE_PRODUCT,
  CREATE_APP_KYC,
  UPDATE_APP_KYC,
  DELETE_APP_KYC,
  DEV_CLEANUP_APP_KYCS,
  CREATE_GATEWAY_SKU,
  UPDATE_GATEWAY_SKU,
  DELETE_GATEWAY_SKU,
  CREATE_GATEWAY_ROUTE,
  UPDATE_GATEWAY_ROUTE,
  DELETE_GATEWAY_ROUTE,
  ATTACH_GATEWAY_SKU_TO_PRODUCT,
  DETACH_GATEWAY_SKU_FROM_PRODUCT,
  UPDATE_PRODUCT_GATEWAY_SKU_ATTACHMENT,
  CREATE_SUPPORT_CASE,
  CLAIM_SUPPORT_CASE,
  ASSIGN_SUPPORT_CASE,
  TRANSITION_SUPPORT_CASE_STATUS,
  ADD_SUPPORT_CASE_COMMENT,
  ACCEPT_SUPPORT_CASE_RESOLUTION,
  UPDATE_TENANT_SUPPORT_CASE_SETTINGS,
  UPDATE_TENANT_ORGANIZATION_SETTINGS,
  ADD_TENANT_MEMBER,
  UPDATE_TENANT_MEMBER,
  REMOVE_TENANT_MEMBER,
  PURGE_TENANT_DATA,
  UPDATE_PLATFORM_TENANT_ORGANIZATION_SETTINGS,
  ADD_PLATFORM_TENANT_ADMIN,
  SET_PLATFORM_TENANT_STATUS,
  CREATE_PLATFORM_TENANT,
  UPSERT_PLATFORM_AGENT_RUNNER_BINDING,
  DELETE_PLATFORM_AGENT_RUNNER_BINDING,
  CREATE_OPERATIONAL_ERROR,
  UPDATE_OPERATIONAL_ERROR,
  DELETE_OPERATIONAL_ERROR,
  BULK_CREATE_OPERATIONAL_ERRORS,
  CREATE_KAFKA_EVENT_TOPIC,
  UPDATE_KAFKA_EVENT_TOPIC,
  DELETE_KAFKA_EVENT_TOPIC,
  CREATE_API_INTAKE,
  UPDATE_API_INTAKE,
  DELETE_API_INTAKE,
  CREATE_PRODUCT_MANAGEMENT_GROUP,
  UPDATE_PRODUCT_MANAGEMENT_GROUP,
  DELETE_PRODUCT_MANAGEMENT_GROUP,
  ADD_PMG_OWNER,
  REMOVE_PMG_OWNER,
  ADD_PMG_MEMBER,
  REMOVE_PMG_MEMBER,
  ASSIGN_PMG_TO_ENTITY,
  UNASSIGN_PMG_FROM_ENTITY,
  BULK_ASSIGN_PMG_TO_ENTITIES,
} from "../mutations/mutations";
import type {
  API,
  AppKyc,
  AppKeyStatus,
  GatewayRoute,
  GatewayRouteSloTarget,
  GatewaySku,
  GatewaySkuStatus,
  GatewayType,
  RouteAuthMechanism,
  DataSensitivity,
  EntityStatus,
  EntityType,
  EntityPmgAssignment,
  ProductManagementGroup,
  ProductGatewaySkuAttachment,
  Category,
  Document,
  GuideSourceFormat,
  LibrarySearchResponse,
  OperationalError,
  OperationalErrorCategory,
  KafkaEventTopic,
  KafkaSchemaFormat,
  SchemaCompatibilityLevel,
  DeveloperGuide,
  DeveloperGuideSection,
  FAQ,
  Link,
  Product,
  ProductCatalogItem,
  ProductSubscription,
  SupportCase,
  SupportCaseComment,
  SupportCaseCommentType,
  SupportCaseStatus,
  TenantSupportCaseSettings,
  TenantOrganizationSettings,
  TenantContactInfo,
  TenantDataExport,
  TenantDataPurgeResult,
  TenantMember,
  TenantMemberStatus,
  PlatformTenant,
  PlatformTenantInsightsSummary,
  AgentRunnerBinding,
} from "../../types";
import type { ApiIntake } from "../../types/apiIntake";

export const apiService = {
  /* ================= APIs ================= */

  getApis: async (): Promise<API[]> => {
    const data = await graphqlRequest<{ apis: API[] }>(GET_APIS);
    return data.apis ?? [];
  },

  getApi: async (uuid: string): Promise<API> => {
    const data = await graphqlRequest<{ api: API }>(GET_API, { uuid });
    return data.api;
  },

  createApi: async (input: {
    tenantId: string;
    name: string;
    version: string;
    status: EntityStatus;
    description?: string;
    specContent?: string;
    tags?: string[];
    architectReview?: string;
  }): Promise<API> => {
    const data = await graphqlRequest<{ createAPI: API }>(CREATE_API, { input });
    return data.createAPI;
  },

  updateApi: async (
    uuid: string,
    input: {
      name?: string;
      version?: string;
      status?: EntityStatus;
      description?: string;
      specContent?: string;
      tags?: string[];
      architectReview?: string;
      documentUuids?: string[];
    },
  ): Promise<API> => {
    const data = await graphqlRequest<{ updateAPI: API }>(UPDATE_API, {
      uuid,
      input,
    });
    return data.updateAPI;
  },

  deleteApi: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteAPI: boolean }>(DELETE_API, {
      uuid,
    });
    return data.deleteAPI;
  },

  /* ================= Products ================= */

  getProducts: async (): Promise<Product[]> => {
    const data = await graphqlRequest<{ products: Product[] }>(GET_PRODUCTS);
    return data.products ?? [];
  },

  getProductCatalog: async (): Promise<ProductCatalogItem[]> => {
    const data = await graphqlRequest<{ productCatalog: ProductCatalogItem[] }>(
      GET_PRODUCT_CATALOG,
    );
    return data.productCatalog ?? [];
  },

  getProduct: async (uuid: string): Promise<Product> => {
    const data = await graphqlRequest<{ product: Product }>(GET_PRODUCT, {
      uuid,
    });
    return data.product;
  },

  createProduct: async (input: any): Promise<Product> => {
    const data = await graphqlRequest<{ createProduct: Product }>(
      CREATE_PRODUCT,
      { input },
    );
    return data.createProduct;
  },

  updateProduct: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      status?: EntityStatus;
      approvalNeeded?: boolean;
      productOwners?: string[];
      apiUuids?: string[];
      categoryUuids?: string[];
      documentUuids?: string[];
      faqUuids?: string[];
      linkUuids?: string[];
      developerGuideUuids?: string[];
    },
  ): Promise<Product> => {
    const data = await graphqlRequest<{ updateProduct: Product }>(
      UPDATE_PRODUCT,
      { uuid, input },
    );
    return data.updateProduct;
  },

  deleteProduct: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteProduct: boolean }>(
      DELETE_PRODUCT,
      { uuid },
    );
    return data.deleteProduct;
  },

  /* ================= FAQs ================= */

  getFaqs: async (): Promise<FAQ[]> => {
    const data = await graphqlRequest<{ faqs: FAQ[] }>(GET_FAQS);
    return data.faqs ?? [];
  },

  getFaq: async (uuid: string): Promise<FAQ> => {
    const data = await graphqlRequest<{ faq: FAQ }>(GET_FAQ, { uuid });
    return data.faq;
  },

  createFaq: async (input: {
    tenantId: string | null;
    name: string;
    description?: string;
    question?: string;
    answer?: string;
    status: EntityStatus;
  }): Promise<FAQ> => {
    const data = await graphqlRequest<{ createFAQ: FAQ }>(CREATE_FAQ, { input });
    return data.createFAQ;
  },

  updateFaq: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      question?: string;
      answer?: string;
      status?: EntityStatus;
    },
  ): Promise<FAQ> => {
    const data = await graphqlRequest<{ updateFAQ: FAQ }>(UPDATE_FAQ, {
      uuid,
      input,
    });
    return data.updateFAQ;
  },

  deleteFaq: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteFAQ: boolean }>(DELETE_FAQ, {
      uuid,
    });
    return data.deleteFAQ;
  },

  /* ================= Developer Guides ================= */

  getDeveloperGuides: async (): Promise<DeveloperGuide[]> => {
    const data = await graphqlRequest<{ developerGuides: DeveloperGuide[] }>(
      GET_DEVELOPER_GUIDES,
    );
    return data.developerGuides ?? [];
  },

  getDeveloperGuide: async (uuid: string): Promise<DeveloperGuide> => {
    const data = await graphqlRequest<{ developerGuide: DeveloperGuide }>(
      GET_DEVELOPER_GUIDE,
      { uuid },
    );
    return data.developerGuide;
  },

  createDeveloperGuide: async (input: {
    tenantId: string | null;
    name: string;
    description?: string;
    status: EntityStatus;
    sections?: DeveloperGuideSection[];
    apiUuids?: string[];
    productUuids?: string[];
  }): Promise<DeveloperGuide> => {
    const data = await graphqlRequest<{ createDeveloperGuide: DeveloperGuide }>(
      CREATE_DEVELOPER_GUIDE,
      { input },
    );
    return data.createDeveloperGuide;
  },

  updateDeveloperGuide: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      status?: EntityStatus;
      sections?: DeveloperGuideSection[];
      apiUuids?: string[];
      productUuids?: string[];
    },
  ): Promise<DeveloperGuide> => {
    const data = await graphqlRequest<{ updateDeveloperGuide: DeveloperGuide }>(
      UPDATE_DEVELOPER_GUIDE,
      { uuid, input },
    );
    return data.updateDeveloperGuide;
  },

  deleteDeveloperGuide: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteDeveloperGuide: boolean }>(
      DELETE_DEVELOPER_GUIDE,
      { uuid },
    );
    return data.deleteDeveloperGuide;
  },

  /* ================= Links ================= */

  getLinks: async (): Promise<Link[]> => {
    const data = await graphqlRequest<{ links: Link[] }>(GET_LINKS);
    return data.links ?? [];
  },

  getLink: async (uuid: string): Promise<Link> => {
    const data = await graphqlRequest<{ link: Link }>(GET_LINK, { uuid });
    return data.link;
  },

  createLink: async (input: {
    tenantId: string | null;
    name: string;
    status: EntityStatus;
    description?: string;
    entityClass?: string;
  }): Promise<Link> => {
    const data = await graphqlRequest<{ createLink: Link }>(CREATE_LINK, {
      input,
    });
    return data.createLink;
  },

  updateLink: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      entityClass?: string;
      status?: EntityStatus;
    },
  ): Promise<Link> => {
    const data = await graphqlRequest<{ updateLink: Link }>(UPDATE_LINK, {
      uuid,
      input,
    });
    return data.updateLink;
  },

  deleteLink: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteLink: boolean }>(DELETE_LINK, {
      uuid,
    });
    return data.deleteLink;
  },

  /* ================= Documents ================= */

  getDocuments: async (): Promise<Document[]> => {
    const data = await graphqlRequest<{ documents: Document[] }>(
      GET_DOCUMENTS,
    );
    return data.documents ?? [];
  },

  getDocument: async (uuid: string): Promise<Document> => {
    const data = await graphqlRequest<{ document: Document }>(GET_DOCUMENT, {
      uuid,
    });
    return data.document;
  },

  createDocument: async (input: {
    tenantId: string;
    name: string;
    description?: string;
    status: EntityStatus;
    tags?: string[];
    entityClass?: string;
    classifications?: string;
    content?: string;
    sourceFormat?: GuideSourceFormat;
    sourceFileName?: string;
    expirationDate?: string;
    productUuids?: string[];
    apiUuids?: string[];
  }): Promise<Document> => {
    const data = await graphqlRequest<{ createDocument: Document }>(
      CREATE_DOCUMENT,
      { input },
    );
    return data.createDocument;
  },

  updateDocument: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      status?: EntityStatus;
      tags?: string[];
      entityClass?: string;
      classifications?: string;
      content?: string;
      sourceFormat?: GuideSourceFormat;
      sourceFileName?: string;
      expirationDate?: string;
      clearExpiration?: boolean;
    },
  ): Promise<Document> => {
    const data = await graphqlRequest<{ updateDocument: Document }>(
      UPDATE_DOCUMENT,
      { uuid, input },
    );
    return data.updateDocument;
  },

  retryDocumentRag: async (uuid: string): Promise<Document> => {
    const data = await graphqlRequest<{ retryDocumentRag: Document }>(
      RETRY_DOCUMENT_RAG,
      { uuid },
    );
    return data.retryDocumentRag;
  },

  searchLibraryDocuments: async (input: {
    query: string;
    limit?: number;
    parentEntityType?: string;
    parentEntityId?: string;
  }): Promise<LibrarySearchResponse> => {
    return cmsRestJson<LibrarySearchResponse>("/documents/library-search", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  deleteDocument: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteDocument: boolean }>(
      DELETE_DOCUMENT,
      { uuid },
    );
    return data.deleteDocument;
  },

  seedArchitectureStandards: async (): Promise<{
    created: number;
    skipped: number;
    total: number;
  }> => {
    const data = await graphqlRequest<{
      seedArchitectureStandards: {
        created: number;
        skipped: number;
        total: number;
      };
    }>(SEED_ARCHITECTURE_STANDARDS);
    return data.seedArchitectureStandards;
  },

  /* ================= Categories ================= */

  getCategories: async (): Promise<Category[]> => {
    const data = await graphqlRequest<{ categories: Category[] }>(
      GET_CATEGORIES,
    );
    return data.categories ?? [];
  },

  getCategory: async (uuid: string): Promise<Category> => {
    const data = await graphqlRequest<{ category: Category }>(GET_CATEGORY, {
      uuid,
    });
    return data.category;
  },

  createCategory: async (input: {
    tenantId: string | null;
    name: string;
    status: EntityStatus;
  }): Promise<Category> => {
    const data = await graphqlRequest<{ createCategory: Category }>(
      CREATE_CATEGORY,
      { input },
    );
    return data.createCategory;
  },

  updateCategory: async (
    uuid: string,
    input: { name?: string; status?: EntityStatus },
  ): Promise<Category> => {
    const data = await graphqlRequest<{ updateCategory: Category }>(
      UPDATE_CATEGORY,
      { uuid, input },
    );
    return data.updateCategory;
  },

  deleteCategory: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteCategory: boolean }>(
      DELETE_CATEGORY,
      { uuid },
    );
    return data.deleteCategory;
  },

  /* ================= Subscriptions ================= */

  getMySubscriptions: async (): Promise<ProductSubscription[]> => {
    const data = await graphqlRequest<{ mySubscriptions: ProductSubscription[] }>(
      GET_MY_SUBSCRIPTIONS,
    );
    return data.mySubscriptions ?? [];
  },

  createSubscription: async (input: {
    productUuids: string[];
    name?: string;
    description?: string;
    status?: EntityStatus;
  }): Promise<ProductSubscription> => {
    const data = await graphqlRequest<{ createSubscription: ProductSubscription }>(
      CREATE_SUBSCRIPTION,
      { input },
    );
    return data.createSubscription;
  },

  getSubscriptionRequests: async (): Promise<ProductSubscription[]> => {
    const data = await graphqlRequest<{ subscriptionRequests: ProductSubscription[] }>(
      GET_SUBSCRIPTION_REQUESTS,
    );
    return data.subscriptionRequests ?? [];
  },

  approveSubscriptionProduct: async (
    subscriptionUuid: string,
    productUuid: string,
  ): Promise<ProductSubscription> => {
    const data = await graphqlRequest<{ approveSubscriptionProduct: ProductSubscription }>(
      APPROVE_SUBSCRIPTION_PRODUCT,
      { subscriptionUuid, productUuid },
    );
    return data.approveSubscriptionProduct;
  },

  revokeSubscriptionProduct: async (
    subscriptionUuid: string,
    productUuid: string,
  ): Promise<ProductSubscription> => {
    const data = await graphqlRequest<{ revokeSubscriptionProduct: ProductSubscription }>(
      REVOKE_SUBSCRIPTION_PRODUCT,
      { subscriptionUuid, productUuid },
    );
    return data.revokeSubscriptionProduct;
  },

  approveSubscription: async (uuid: string): Promise<ProductSubscription> => {
    const data = await graphqlRequest<{ approveSubscription: ProductSubscription }>(
      APPROVE_SUBSCRIPTION,
      { uuid },
    );
    return data.approveSubscription;
  },

  revokeSubscription: async (uuid: string): Promise<ProductSubscription> => {
    const data = await graphqlRequest<{ revokeSubscription: ProductSubscription }>(
      REVOKE_SUBSCRIPTION,
      { uuid },
    );
    return data.revokeSubscription;
  },

  /* ================= App KYC ================= */

  getAppKycs: async (): Promise<AppKyc[]> => {
    const data = await graphqlRequest<{ appKycs: AppKyc[] }>(GET_APP_KYCS);
    return data.appKycs ?? [];
  },

  getAppKyc: async (uuid: string): Promise<AppKyc> => {
    const data = await graphqlRequest<{ appKyc: AppKyc }>(GET_APP_KYC, { uuid });
    return data.appKyc;
  },

  createAppKyc: async (input: {
    tenantId: string | null;
    appKeyId: string;
    providerGatewayType: string;
    providerGatewayIdentifier: string;
    appKeyStatus?: AppKeyStatus;
    description?: string;
    keyCreatedOn?: string;
    keyExpiresOn?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactCompanyName?: string;
    contactCompanyWebsite?: string;
    bmContactName?: string;
    bmContactEmail?: string;
    itSupportName?: string;
    itSupportEmail?: string;
  }): Promise<AppKyc> => {
    const data = await graphqlRequest<{ createAppKyc: AppKyc }>(CREATE_APP_KYC, {
      input,
    });
    return data.createAppKyc;
  },

  updateAppKyc: async (
    uuid: string,
    input: {
      appKeyId?: string;
      providerGatewayType?: string;
      providerGatewayIdentifier?: string;
      appKeyStatus?: AppKeyStatus;
      description?: string;
      keyCreatedOn?: string;
      keyExpiresOn?: string;
      contactName?: string;
      contactEmail?: string;
      contactPhone?: string;
      contactCompanyName?: string;
      contactCompanyWebsite?: string;
      bmContactName?: string;
      bmContactEmail?: string;
      itSupportName?: string;
      itSupportEmail?: string;
    },
  ): Promise<AppKyc> => {
    const data = await graphqlRequest<{ updateAppKyc: AppKyc }>(UPDATE_APP_KYC, {
      uuid,
      input,
    });
    return data.updateAppKyc;
  },

  deleteAppKyc: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteAppKyc: boolean }>(DELETE_APP_KYC, {
      uuid,
    });
    return data.deleteAppKyc;
  },

  devCleanupAppKycs: async (): Promise<number> => {
    const data = await graphqlRequest<{ devCleanupAppKycs: number }>(
      DEV_CLEANUP_APP_KYCS,
    );
    return data.devCleanupAppKycs;
  },

  getGatewaySkus: async (): Promise<GatewaySku[]> => {
    const data = await graphqlRequest<{ gatewaySkus: GatewaySku[] }>(GET_GATEWAY_SKUS);
    return data.gatewaySkus ?? [];
  },

  getGatewaySku: async (uuid: string): Promise<GatewaySku> => {
    const data = await graphqlRequest<{ gatewaySku: GatewaySku }>(GET_GATEWAY_SKU, { uuid });
    return data.gatewaySku;
  },

  createGatewaySku: async (input: {
    tenantId: string | null;
    gatewayProductIdentifier: string;
    gatewayProductName: string;
    gatewayCommonName: string;
    gatewayId: string;
    gatewayType: GatewayType;
    envName: string;
    description?: string;
    skuStatus?: GatewaySkuStatus;
  }): Promise<GatewaySku> => {
    const data = await graphqlRequest<{ createGatewaySku: GatewaySku }>(CREATE_GATEWAY_SKU, {
      input,
    });
    return data.createGatewaySku;
  },

  updateGatewaySku: async (
    uuid: string,
    input: {
      gatewayProductIdentifier?: string;
      gatewayProductName?: string;
      gatewayCommonName?: string;
      gatewayId?: string;
      gatewayType?: GatewayType;
      envName?: string;
      description?: string;
      skuStatus?: GatewaySkuStatus;
    },
  ): Promise<GatewaySku> => {
    const data = await graphqlRequest<{ updateGatewaySku: GatewaySku }>(UPDATE_GATEWAY_SKU, {
      uuid,
      input,
    });
    return data.updateGatewaySku;
  },

  deleteGatewaySku: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteGatewaySku: boolean }>(DELETE_GATEWAY_SKU, {
      uuid,
    });
    return data.deleteGatewaySku;
  },

  getGatewayRoutes: async (): Promise<GatewayRoute[]> => {
    const data = await graphqlRequest<{ gatewayRoutes: GatewayRoute[] }>(GET_GATEWAY_ROUTES);
    return data.gatewayRoutes ?? [];
  },

  getGatewayRoute: async (uuid: string): Promise<GatewayRoute> => {
    const data = await graphqlRequest<{ gatewayRoute: GatewayRoute }>(GET_GATEWAY_ROUTE, { uuid });
    return data.gatewayRoute;
  },

  searchGatewayRoutes: async (query: string): Promise<GatewayRoute[]> => {
    const data = await graphqlRequest<{ searchGatewayRoutes: GatewayRoute[] }>(
      SEARCH_GATEWAY_ROUTES,
      { query },
    );
    return data.searchGatewayRoutes ?? [];
  },

  findPotentialDuplicateGatewayRoutes: async (params: {
    envName: string;
    gatewayId: string;
    basePath: string;
    excludeUuid?: string;
  }): Promise<GatewayRoute[]> => {
    const data = await graphqlRequest<{ potentialDuplicateGatewayRoutes: GatewayRoute[] }>(
      POTENTIAL_DUPLICATE_GATEWAY_ROUTES,
      params,
    );
    return data.potentialDuplicateGatewayRoutes ?? [];
  },

  createGatewayRoute: async (input: {
    tenantId: string | null;
    status: EntityStatus;
    basePath: string;
    upstreamServer: string;
    upstreamPath: string;
    envName: string;
    gatewayAuthMechanism: RouteAuthMechanism;
    upstreamAuthMechanism: RouteAuthMechanism;
    gatewayId?: string;
    gatewaySkuUuid?: string;
    name?: string;
    description?: string;
    gatewayAuthNotes?: string;
    upstreamAuthNotes?: string;
    purpose?: string;
    dataSensitivity?: DataSensitivity;
    notes?: string;
    sloTargets?: GatewayRouteSloTarget[];
    productUuids?: string[];
    apiUuids?: string[];
  }): Promise<GatewayRoute> => {
    const data = await graphqlRequest<{ createGatewayRoute: GatewayRoute }>(CREATE_GATEWAY_ROUTE, {
      input,
    });
    return data.createGatewayRoute;
  },

  updateGatewayRoute: async (
    uuid: string,
    input: Record<string, unknown>,
  ): Promise<GatewayRoute> => {
    const data = await graphqlRequest<{ updateGatewayRoute: GatewayRoute }>(UPDATE_GATEWAY_ROUTE, {
      uuid,
      input,
    });
    return data.updateGatewayRoute;
  },

  deleteGatewayRoute: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteGatewayRoute: boolean }>(DELETE_GATEWAY_ROUTE, {
      uuid,
    });
    return data.deleteGatewayRoute;
  },

  attachGatewaySkuToProduct: async (
    productUuid: string,
    gatewaySkuUuid: string,
    attachmentName?: string,
  ): Promise<ProductGatewaySkuAttachment> => {
    const data = await graphqlRequest<{
      attachGatewaySkuToProduct: ProductGatewaySkuAttachment;
    }>(ATTACH_GATEWAY_SKU_TO_PRODUCT, {
      productUuid,
      gatewaySkuUuid,
      attachmentName: attachmentName?.trim() || null,
    });
    return data.attachGatewaySkuToProduct;
  },

  detachGatewaySkuFromProduct: async (
    productUuid: string,
    gatewaySkuUuid: string,
  ): Promise<boolean> => {
    const data = await graphqlRequest<{ detachGatewaySkuFromProduct: boolean }>(
      DETACH_GATEWAY_SKU_FROM_PRODUCT,
      { productUuid, gatewaySkuUuid },
    );
    return data.detachGatewaySkuFromProduct;
  },

  updateProductGatewaySkuAttachment: async (
    attachmentUuid: string,
    attachmentName?: string,
  ): Promise<ProductGatewaySkuAttachment> => {
    const data = await graphqlRequest<{
      updateProductGatewaySkuAttachment: ProductGatewaySkuAttachment;
    }>(UPDATE_PRODUCT_GATEWAY_SKU_ATTACHMENT, {
      attachmentUuid,
      attachmentName: attachmentName?.trim() || null,
    });
    return data.updateProductGatewaySkuAttachment;
  },

  /* ================= Support Cases ================= */

  getMySupportCases: async (): Promise<SupportCase[]> => {
    const data = await graphqlRequest<{ mySupportCases: SupportCase[] }>(
      GET_MY_SUPPORT_CASES,
    );
    return data.mySupportCases ?? [];
  },

  getAgentSupportCases: async (): Promise<SupportCase[]> => {
    const data = await graphqlRequest<{ agentSupportCases: SupportCase[] }>(
      GET_AGENT_SUPPORT_CASES,
    );
    return data.agentSupportCases ?? [];
  },

  getSupportCase: async (uuid: string): Promise<SupportCase> => {
    const data = await graphqlRequest<{ supportCase: SupportCase }>(
      GET_SUPPORT_CASE,
      { uuid },
    );
    return data.supportCase;
  },

  createSupportCase: async (input: {
    tenantId: string | null;
    name: string;
    description?: string;
    productUuid?: string;
    tags?: string[];
    classifications?: string;
    entityClass?: string;
  }): Promise<SupportCase> => {
    const data = await graphqlRequest<{ createSupportCase: SupportCase }>(
      CREATE_SUPPORT_CASE,
      { input },
    );
    return data.createSupportCase;
  },

  claimSupportCase: async (uuid: string): Promise<SupportCase> => {
    const data = await graphqlRequest<{ claimSupportCase: SupportCase }>(
      CLAIM_SUPPORT_CASE,
      { uuid },
    );
    return data.claimSupportCase;
  },

  assignSupportCase: async (
    uuid: string,
    assigneeEmail: string,
  ): Promise<SupportCase> => {
    const data = await graphqlRequest<{ assignSupportCase: SupportCase }>(
      ASSIGN_SUPPORT_CASE,
      { uuid, assigneeEmail },
    );
    return data.assignSupportCase;
  },

  transitionSupportCaseStatus: async (
    uuid: string,
    status: SupportCaseStatus,
  ): Promise<SupportCase> => {
    const data = await graphqlRequest<{
      transitionSupportCaseStatus: SupportCase;
    }>(TRANSITION_SUPPORT_CASE_STATUS, { uuid, status });
    return data.transitionSupportCaseStatus;
  },

  addSupportCaseComment: async (
    uuid: string,
    body: string,
    commentType: SupportCaseCommentType,
  ): Promise<SupportCaseComment> => {
    const data = await graphqlRequest<{ addSupportCaseComment: SupportCaseComment }>(
      ADD_SUPPORT_CASE_COMMENT,
      { uuid, body, commentType },
    );
    return data.addSupportCaseComment;
  },

  acceptSupportCaseResolution: async (uuid: string): Promise<SupportCase> => {
    const data = await graphqlRequest<{ acceptSupportCaseResolution: SupportCase }>(
      ACCEPT_SUPPORT_CASE_RESOLUTION,
      { uuid },
    );
    return data.acceptSupportCaseResolution;
  },

  getTenantSupportCaseSettings: async (): Promise<TenantSupportCaseSettings> => {
    const data = await graphqlRequest<{
      tenantSupportCaseSettings: TenantSupportCaseSettings;
    }>(GET_TENANT_SUPPORT_CASE_SETTINGS);
    return data.tenantSupportCaseSettings;
  },

  updateTenantSupportCaseSettings: async (
    autoCloseResolvedDays: number,
  ): Promise<TenantSupportCaseSettings> => {
    const data = await graphqlRequest<{
      updateTenantSupportCaseSettings: TenantSupportCaseSettings;
    }>(UPDATE_TENANT_SUPPORT_CASE_SETTINGS, { autoCloseResolvedDays });
    return data.updateTenantSupportCaseSettings;
  },

  getTenantOrganizationSettings: async (): Promise<TenantOrganizationSettings> => {
    const data = await graphqlRequest<{
      tenantOrganizationSettings: TenantOrganizationSettings;
    }>(GET_TENANT_ORGANIZATION_SETTINGS);
    return data.tenantOrganizationSettings;
  },

  updateTenantOrganizationSettings: async (input: {
    companyName?: string;
    address?: string;
    website?: string;
    phone?: string;
    contactEmail?: string;
  }): Promise<TenantOrganizationSettings> => {
    const data = await graphqlRequest<{
      updateTenantOrganizationSettings: TenantOrganizationSettings;
    }>(UPDATE_TENANT_ORGANIZATION_SETTINGS, { input });
    return data.updateTenantOrganizationSettings;
  },

  getTenantMembers: async (): Promise<TenantMember[]> => {
    const data = await graphqlRequest<{ tenantMembers: TenantMember[] }>(
      GET_TENANT_MEMBERS,
    );
    return data.tenantMembers;
  },

  getTenantContactInfo: async (): Promise<TenantContactInfo> => {
    const data = await graphqlRequest<{ tenantContactInfo: TenantContactInfo }>(
      GET_TENANT_CONTACT_INFO,
    );
    return data.tenantContactInfo;
  },

  addTenantMember: async (input: {
    email: string;
    displayName?: string;
    roles: string[];
  }): Promise<TenantMember> => {
    const data = await graphqlRequest<{ addTenantMember: TenantMember }>(
      ADD_TENANT_MEMBER,
      { input },
    );
    return data.addTenantMember;
  },

  updateTenantMember: async (input: {
    email: string;
    displayName?: string;
    roles?: string[];
    status?: TenantMemberStatus;
  }): Promise<TenantMember> => {
    const data = await graphqlRequest<{ updateTenantMember: TenantMember }>(
      UPDATE_TENANT_MEMBER,
      { input },
    );
    return data.updateTenantMember;
  },

  removeTenantMember: async (email: string): Promise<TenantMember> => {
    const data = await graphqlRequest<{ removeTenantMember: TenantMember }>(
      REMOVE_TENANT_MEMBER,
      { email },
    );
    return data.removeTenantMember;
  },

  exportTenantData: async (): Promise<TenantDataExport> => {
    const data = await graphqlRequest<{ tenantDataExport: TenantDataExport }>(
      GET_TENANT_DATA_EXPORT,
    );
    return data.tenantDataExport;
  },

  purgeTenantData: async (confirmation: string): Promise<TenantDataPurgeResult> => {
    const data = await graphqlRequest<{ purgeTenantData: TenantDataPurgeResult }>(
      PURGE_TENANT_DATA,
      { confirmation },
    );
    return data.purgeTenantData;
  },

  getPlatformTenants: async (): Promise<PlatformTenant[]> => {
    const data = await graphqlRequest<{ platformTenants: PlatformTenant[] }>(
      GET_PLATFORM_TENANTS,
    );
    return data.platformTenants;
  },

  getPlatformTenantInsights: async (): Promise<PlatformTenantInsightsSummary> => {
    const data = await graphqlRequest<{
      platformTenantInsights: PlatformTenantInsightsSummary;
    }>(GET_PLATFORM_TENANT_INSIGHTS);
    return data.platformTenantInsights;
  },

  setPlatformTenantStatus: async (
    tenantId: string,
    enabled: boolean,
  ): Promise<PlatformTenant> => {
    const data = await graphqlRequest<{ setPlatformTenantStatus: PlatformTenant }>(
      SET_PLATFORM_TENANT_STATUS,
      { tenantId, enabled },
    );
    return data.setPlatformTenantStatus;
  },

  createPlatformTenant: async (input: {
    tenantId: string;
    displayName?: string;
  }): Promise<PlatformTenant> => {
    const data = await graphqlRequest<{ createPlatformTenant: PlatformTenant }>(
      CREATE_PLATFORM_TENANT,
      { input },
    );
    return data.createPlatformTenant;
  },

  getPlatformTenantOrganizationSettings: async (
    tenantId: string,
  ): Promise<TenantOrganizationSettings> => {
    const data = await graphqlRequest<{
      platformTenantOrganizationSettings: TenantOrganizationSettings;
    }>(GET_PLATFORM_TENANT_ORGANIZATION_SETTINGS, { tenantId });
    return data.platformTenantOrganizationSettings;
  },

  updatePlatformTenantOrganizationSettings: async (
    tenantId: string,
    input: {
      companyName?: string;
      address?: string;
      website?: string;
      phone?: string;
      contactEmail?: string;
    },
  ): Promise<TenantOrganizationSettings> => {
    const data = await graphqlRequest<{
      updatePlatformTenantOrganizationSettings: TenantOrganizationSettings;
    }>(UPDATE_PLATFORM_TENANT_ORGANIZATION_SETTINGS, { tenantId, input });
    return data.updatePlatformTenantOrganizationSettings;
  },

  getPlatformTenantAdmins: async (tenantId: string): Promise<TenantMember[]> => {
    const data = await graphqlRequest<{ platformTenantAdmins: TenantMember[] }>(
      GET_PLATFORM_TENANT_ADMINS,
      { tenantId },
    );
    return data.platformTenantAdmins;
  },

  addPlatformTenantAdmin: async (
    tenantId: string,
    input: { email: string; displayName?: string },
  ): Promise<TenantMember> => {
    const data = await graphqlRequest<{ addPlatformTenantAdmin: TenantMember }>(
      ADD_PLATFORM_TENANT_ADMIN,
      { tenantId, input },
    );
    return data.addPlatformTenantAdmin;
  },

  getPlatformAgentRunnerBindings: async (
    tenantId?: string | null,
  ): Promise<AgentRunnerBinding[]> => {
    const data = await graphqlRequest<{
      platformAgentRunnerBindings: AgentRunnerBinding[];
    }>(GET_PLATFORM_AGENT_RUNNER_BINDINGS, {
      tenantId: tenantId || null,
    });
    return data.platformAgentRunnerBindings ?? [];
  },

  upsertPlatformAgentRunnerBinding: async (input: {
    tenantId?: string | null;
    agentId?: string | null;
    basePath: string;
  }): Promise<AgentRunnerBinding> => {
    const data = await graphqlRequest<{
      upsertPlatformAgentRunnerBinding: AgentRunnerBinding;
    }>(UPSERT_PLATFORM_AGENT_RUNNER_BINDING, {
      tenantId: input.tenantId || null,
      agentId: input.agentId || null,
      basePath: input.basePath,
    });
    return data.upsertPlatformAgentRunnerBinding;
  },

  deletePlatformAgentRunnerBinding: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{
      deletePlatformAgentRunnerBinding: boolean;
    }>(DELETE_PLATFORM_AGENT_RUNNER_BINDING, { uuid });
    return data.deletePlatformAgentRunnerBinding;
  },

  resolvedAgentRunnerBase: async (agentId: string): Promise<string> => {
    const data = await graphqlRequest<{ resolvedAgentRunnerBase: string }>(
      RESOLVED_AGENT_RUNNER_BASE,
      { agentId },
    );
    return data.resolvedAgentRunnerBase;
  },

  /* ================= Operational Errors ================= */

  getOperationalErrors: async (): Promise<OperationalError[]> => {
    const data = await graphqlRequest<{ operationalErrors: OperationalError[] }>(
      GET_OPERATIONAL_ERRORS,
    );
    return data.operationalErrors ?? [];
  },

  searchOperationalErrors: async (query: string): Promise<OperationalError[]> => {
    const data = await graphqlRequest<{
      searchOperationalErrors: OperationalError[];
    }>(SEARCH_OPERATIONAL_ERRORS, { query });
    return data.searchOperationalErrors ?? [];
  },

  getOperationalError: async (uuid: string): Promise<OperationalError> => {
    const data = await graphqlRequest<{ operationalError: OperationalError }>(
      GET_OPERATIONAL_ERROR,
      { uuid },
    );
    return data.operationalError;
  },

  createOperationalError: async (input: {
    tenantId: string;
    name?: string;
    description?: string;
    status: EntityStatus;
    tags?: string[];
    code?: string;
    httpStatus?: number;
    category?: OperationalErrorCategory;
    message?: string;
    remediation?: string;
    errorStrings?: string[];
    productUuids?: string[];
    apiUuids?: string[];
  }): Promise<OperationalError> => {
    const data = await graphqlRequest<{ createOperationalError: OperationalError }>(
      CREATE_OPERATIONAL_ERROR,
      { input },
    );
    return data.createOperationalError;
  },

  updateOperationalError: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      status?: EntityStatus;
      tags?: string[];
      code?: string;
      httpStatus?: number;
      category?: OperationalErrorCategory;
      message?: string;
      remediation?: string;
      errorStrings?: string[];
      productUuids?: string[];
      apiUuids?: string[];
    },
  ): Promise<OperationalError> => {
    const data = await graphqlRequest<{ updateOperationalError: OperationalError }>(
      UPDATE_OPERATIONAL_ERROR,
      { uuid, input },
    );
    return data.updateOperationalError;
  },

  deleteOperationalError: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteOperationalError: boolean }>(
      DELETE_OPERATIONAL_ERROR,
      { uuid },
    );
    return data.deleteOperationalError;
  },

  bulkCreateOperationalErrors: async (
    inputs: Array<{
      tenantId: string;
      name?: string;
      description?: string;
      status: EntityStatus;
      code?: string;
      httpStatus?: number;
      category?: OperationalErrorCategory;
      message?: string;
      remediation?: string;
      errorStrings?: string[];
      productUuids?: string[];
      apiUuids?: string[];
    }>,
  ): Promise<OperationalError[]> => {
    const data = await graphqlRequest<{
      bulkCreateOperationalErrors: OperationalError[];
    }>(BULK_CREATE_OPERATIONAL_ERRORS, { inputs });
    return data.bulkCreateOperationalErrors ?? [];
  },

  /* ================= Kafka Event Topics ================= */

  getKafkaEventTopics: async (): Promise<KafkaEventTopic[]> => {
    const data = await graphqlRequest<{ kafkaEventTopics: KafkaEventTopic[] }>(
      GET_KAFKA_EVENT_TOPICS,
    );
    return data.kafkaEventTopics ?? [];
  },

  searchKafkaEventTopics: async (query: string): Promise<KafkaEventTopic[]> => {
    const data = await graphqlRequest<{ searchKafkaEventTopics: KafkaEventTopic[] }>(
      SEARCH_KAFKA_EVENT_TOPICS,
      { query },
    );
    return data.searchKafkaEventTopics ?? [];
  },

  getKafkaEventTopic: async (uuid: string): Promise<KafkaEventTopic> => {
    const data = await graphqlRequest<{ kafkaEventTopic: KafkaEventTopic }>(
      GET_KAFKA_EVENT_TOPIC,
      { uuid },
    );
    return data.kafkaEventTopic;
  },

  createKafkaEventTopic: async (input: {
    tenantId: string;
    name: string;
    description?: string;
    status: EntityStatus;
    tags?: string[];
    classifications?: string;
    entityClass?: string;
    environment: string;
    purpose?: string;
    schemaFormat: KafkaSchemaFormat;
    schemaContent: string;
    schemaVersion?: string;
    schemaSubject?: string;
    schemaRegistryUrl?: string;
    compatibilityLevel?: SchemaCompatibilityLevel;
    keySchemaFormat?: KafkaSchemaFormat;
    keySchemaContent?: string;
    partitionCount?: number;
    replicationFactor?: number;
    retentionMs?: string;
    samplePayload?: string;
    dataSensitivity?: DataSensitivity;
    notes?: string;
    productUuids?: string[];
    apiUuids?: string[];
  }): Promise<KafkaEventTopic> => {
    const data = await graphqlRequest<{ createKafkaEventTopic: KafkaEventTopic }>(
      CREATE_KAFKA_EVENT_TOPIC,
      { input },
    );
    return data.createKafkaEventTopic;
  },

  updateKafkaEventTopic: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      status?: EntityStatus;
      tags?: string[];
      classifications?: string;
      entityClass?: string;
      environment?: string;
      purpose?: string;
      schemaFormat?: KafkaSchemaFormat;
      schemaContent?: string;
      schemaVersion?: string;
      schemaSubject?: string;
      schemaRegistryUrl?: string;
      compatibilityLevel?: SchemaCompatibilityLevel;
      keySchemaFormat?: KafkaSchemaFormat;
      keySchemaContent?: string;
      partitionCount?: number;
      replicationFactor?: number;
      retentionMs?: string;
      samplePayload?: string;
      dataSensitivity?: DataSensitivity;
      notes?: string;
      productUuids?: string[];
      apiUuids?: string[];
    },
  ): Promise<KafkaEventTopic> => {
    const data = await graphqlRequest<{ updateKafkaEventTopic: KafkaEventTopic }>(
      UPDATE_KAFKA_EVENT_TOPIC,
      { uuid, input },
    );
    return data.updateKafkaEventTopic;
  },

  deleteKafkaEventTopic: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteKafkaEventTopic: boolean }>(
      DELETE_KAFKA_EVENT_TOPIC,
      { uuid },
    );
    return data.deleteKafkaEventTopic;
  },

  /* ================= API Intakes ================= */

  getApiIntakes: async (): Promise<ApiIntake[]> => {
    const data = await graphqlRequest<{ apiIntakes: ApiIntake[] }>(GET_API_INTAKES);
    return data.apiIntakes ?? [];
  },

  getApiIntake: async (uuid: string): Promise<ApiIntake> => {
    const data = await graphqlRequest<{ apiIntake: ApiIntake }>(GET_API_INTAKE, { uuid });
    return data.apiIntake;
  },

  createApiIntake: async (input: {
    tenantId: string;
    name: string;
    description?: string;
    status: EntityStatus;
    requirementText: string;
    relatedDocsMarkdown?: string;
    evaluation?: string;
    coverage?: "NONE" | "PARTIAL" | "FULL";
    coveragePercent?: number;
    recommendedChangePath?:
      | "REUSE_EXISTING"
      | "EXTEND_EXISTING"
      | "COMPOSE_MULTIPLE"
      | "NEW_API";
    recommendedApiUuid?: string;
    matchedApis?: Array<{
      apiUuid: string;
      coveragePercent?: number;
      matchRole?: "PRIMARY" | "SUPPORTING";
      notes?: string;
    }>;
  }): Promise<ApiIntake> => {
    const data = await graphqlRequest<{ createApiIntake: ApiIntake }>(CREATE_API_INTAKE, {
      input,
    });
    return data.createApiIntake;
  },

  updateApiIntake: async (
    uuid: string,
    input: {
      name?: string;
      description?: string;
      status?: EntityStatus;
      requirementText?: string;
      relatedDocsMarkdown?: string;
      evaluation?: string;
      coverage?: "NONE" | "PARTIAL" | "FULL";
      coveragePercent?: number;
      recommendedChangePath?:
        | "REUSE_EXISTING"
        | "EXTEND_EXISTING"
        | "COMPOSE_MULTIPLE"
        | "NEW_API";
      recommendedApiUuid?: string;
      matchedApis?: Array<{
        apiUuid: string;
        coveragePercent?: number;
        matchRole?: "PRIMARY" | "SUPPORTING";
        notes?: string;
      }>;
    },
  ): Promise<ApiIntake> => {
    const data = await graphqlRequest<{ updateApiIntake: ApiIntake }>(UPDATE_API_INTAKE, {
      uuid,
      input,
    });
    return data.updateApiIntake;
  },

  deleteApiIntake: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteApiIntake: boolean }>(DELETE_API_INTAKE, {
      uuid,
    });
    return data.deleteApiIntake;
  },

  getProductManagementGroups: async (): Promise<ProductManagementGroup[]> => {
    const data = await graphqlRequest<{ productManagementGroups: ProductManagementGroup[] }>(
      GET_PRODUCT_MANAGEMENT_GROUPS,
    );
    return data.productManagementGroups ?? [];
  },

  getProductManagementGroup: async (uuid: string): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ productManagementGroup: ProductManagementGroup }>(
      GET_PRODUCT_MANAGEMENT_GROUP,
      { uuid },
    );
    return data.productManagementGroup;
  },

  createProductManagementGroup: async (input: {
    name: string;
    description?: string;
  }): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ createProductManagementGroup: ProductManagementGroup }>(
      CREATE_PRODUCT_MANAGEMENT_GROUP,
      { input },
    );
    return data.createProductManagementGroup;
  },

  updateProductManagementGroup: async (
    uuid: string,
    input: { name?: string; description?: string },
  ): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ updateProductManagementGroup: ProductManagementGroup }>(
      UPDATE_PRODUCT_MANAGEMENT_GROUP,
      { uuid, input },
    );
    return data.updateProductManagementGroup;
  },

  deleteProductManagementGroup: async (uuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ deleteProductManagementGroup: boolean }>(
      DELETE_PRODUCT_MANAGEMENT_GROUP,
      { uuid },
    );
    return data.deleteProductManagementGroup;
  },

  addPmgOwner: async (pmgUuid: string, userEmail: string): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ addPmgOwner: ProductManagementGroup }>(
      ADD_PMG_OWNER,
      { pmgUuid, userEmail },
    );
    return data.addPmgOwner;
  },

  removePmgOwner: async (pmgUuid: string, userEmail: string): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ removePmgOwner: ProductManagementGroup }>(
      REMOVE_PMG_OWNER,
      { pmgUuid, userEmail },
    );
    return data.removePmgOwner;
  },

  addPmgMember: async (pmgUuid: string, userEmail: string): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ addPmgMember: ProductManagementGroup }>(
      ADD_PMG_MEMBER,
      { pmgUuid, userEmail },
    );
    return data.addPmgMember;
  },

  removePmgMember: async (pmgUuid: string, userEmail: string): Promise<ProductManagementGroup> => {
    const data = await graphqlRequest<{ removePmgMember: ProductManagementGroup }>(
      REMOVE_PMG_MEMBER,
      { pmgUuid, userEmail },
    );
    return data.removePmgMember;
  },

  assignPmgToEntity: async (input: {
    pmgUuid: string;
    entityUuid: string;
    entityType: EntityType;
  }): Promise<boolean> => {
    const data = await graphqlRequest<{ assignPmgToEntity: boolean }>(
      ASSIGN_PMG_TO_ENTITY,
      { input },
    );
    return data.assignPmgToEntity;
  },

  unassignPmgFromEntity: async (pmgUuid: string, entityUuid: string): Promise<boolean> => {
    const data = await graphqlRequest<{ unassignPmgFromEntity: boolean }>(
      UNASSIGN_PMG_FROM_ENTITY,
      { pmgUuid, entityUuid },
    );
    return data.unassignPmgFromEntity;
  },

  getEntityPmgAssignments: async (entityUuid: string): Promise<EntityPmgAssignment[]> => {
    const data = await graphqlRequest<{ entityPmgAssignments: EntityPmgAssignment[] }>(
      GET_ENTITY_PMG_ASSIGNMENTS,
      { entityUuid },
    );
    return data.entityPmgAssignments ?? [];
  },

  getPmgAssignedAssets: async (pmgUuid: string): Promise<EntityPmgAssignment[]> => {
    const data = await graphqlRequest<{ pmgAssignedAssets: EntityPmgAssignment[] }>(
      GET_PMG_ASSIGNED_ASSETS,
      { pmgUuid },
    );
    return data.pmgAssignedAssets ?? [];
  },

  bulkAssignPmgToEntities: async (
    pmgUuid: string,
    items: Array<{ entityUuid: string; entityType: EntityType }>,
  ): Promise<number> => {
    const data = await graphqlRequest<{ bulkAssignPmgToEntities: number }>(
      BULK_ASSIGN_PMG_TO_ENTITIES,
      { pmgUuid, items },
    );
    return data.bulkAssignPmgToEntities;
  },
};
